import { ONDO_BNB_ASSET_IDS, ONDO_ETHEREUM_ASSET_IDS } from "@repo/constants";

const ONDO_DIRECT_ASSET_KEYS = new Set<string>(
  [...ONDO_BNB_ASSET_IDS, ...ONDO_ETHEREUM_ASSET_IDS].map((assetId) =>
    assetId.toLowerCase(),
  ),
);

export interface SellRoutableAsset {
  chainId: number;
  /** Lowercase EVM token address. */
  tokenAddress: string;
}

export interface PortfolioSellExecutionBatch<T extends SellRoutableAsset> {
  kind: "ondo" | "standard";
  assets: T[];
}

export const isOndoDirectSellAsset = (asset: SellRoutableAsset): boolean =>
  ONDO_DIRECT_ASSET_KEYS.has(
    `${asset.tokenAddress.toLowerCase()}:${asset.chainId.toString(10)}`,
  );

/**
 * Keep Ondo Direct assets out of the standard withdraw-as-USDC request.
 *
 * The backend selects Ondo Direct only when every swap asset in a request is
 * Ondo-eligible. A mixed request otherwise falls through to LiFi, which cannot
 * execute the Ondo leg. Separate requests let the backend select the correct
 * execution method for each bucket without changing the API contract.
 */
export const buildPortfolioSellExecutionBatches = <T extends SellRoutableAsset>(
  assets: readonly T[],
): PortfolioSellExecutionBatch<T>[] => {
  const ondoAssets: T[] = [];
  const standardAssets: T[] = [];

  for (const asset of assets) {
    if (isOndoDirectSellAsset(asset)) {
      ondoAssets.push(asset);
    } else {
      standardAssets.push(asset);
    }
  }

  const batches: PortfolioSellExecutionBatch<T>[] = [];
  if (ondoAssets.length > 0) {
    batches.push({ kind: "ondo", assets: ondoAssets });
  }
  if (standardAssets.length > 0) {
    batches.push({ kind: "standard", assets: standardAssets });
  }
  return batches;
};
